import { VoidCallback1 } from '@deqo/foundation';
import Downshift from 'downshift';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars';
import styled, { css } from 'styled-components';
import { LFC } from '../Legacy';
import { Colors } from './Colors';
import { Float } from './Float';
import { GoWizard } from './GoWizard';
import { Prompt } from './Prompt';

const XScrollbars = Scrollbars as any;

export interface GoItem {
    name: string;
    label: string;
    wizard?: GoWizard;
}

export type GoProvider = (input?: string | null) => GoItem[];

export type GoResult = { success: boolean, item?: GoItem, params?: any[] };

export type GoCallback = (result: GoResult) => void;

type GoContextProps = { provider: GoProvider, callback: GoCallback };
const GoContext = React.createContext(null! as GoContextProps);

const Mount = styled.div`
    position: absolute;
    top: 0.5rem;
    left: 50%;
    width: 60rem;
    margin-left: -30rem;
`;

const Input = styled.input.attrs({ type: 'text', autocomplete: 'off' })`
    position: relative;
    box-sizing: border-box;
    outline: 0;
    margin: 0.5rem 0;
    padding: 0 0.5rem;
    width: 100%;
    color: ${Colors.gray700};
    font-size: 0.875rem;
`;

const List = styled.div<{ count: number }>`
    padding-bottom: ${p => p.count ? '.25rem' : '0'};
`;

const Item = styled.div<{ active?: boolean, selected?: boolean }>`
    padding: 0.5rem;
    font-size: 0.875rem;
    color: ${Colors.gray700};
    cursor: pointer;

    &:hover { background: ${Colors.hover}; }
    &:active { background: ${Colors.active}; }
    ${p => !p.active ? '' : css`background: ${Colors.hover};`}
`;

const Selector: LFC<{ callback: VoidCallback1<GoItem | null> }> =
    ({ callback }) => {

        const { provider } = useContext(GoContext);
        const itemToString = (item: GoItem | null) => item?.label || '';

        return <Prompt ignoreEnter onAction={() => callback(null)}>
            <Downshift onChange={callback} itemToString={itemToString} defaultHighlightedIndex={0} defaultIsOpen>
                {({
                    getRootProps,
                    getInputProps,
                    getItemProps,
                    getMenuProps,
                    /* isOpen,*/
                    inputValue,
                    highlightedIndex,
                }) => {
                    const items = provider(inputValue);
                    return <Mount {...getRootProps()}>
                        <Float shadow="lg">
                            <Input {...getInputProps()} role="searchbox" autoFocus />
                            <List count={items.length} {...getMenuProps()}>
                                <XScrollbars autoHeight autoHeightMax={384}>
                                    {items.map((item, index) =>
                                        <Item
                                            key={item.name}
                                            active={index === highlightedIndex}
                                            {...getItemProps({ item, index })}
                                        >
                                            {item.label}
                                        </Item>
                                    )}
                                </XScrollbars>
                            </List>
                        </Float>
                    </Mount>;
                }}
            </Downshift>
        </Prompt>;
    };

interface WizardProps { wizard: GoWizard, callback: VoidCallback1<any[] | null> }
const Wizard = ({ wizard, callback }: WizardProps) => {

    const [index, setIndex] = useState(0);
    const [value, setValue] = useState([] as any[]);
    const [input, setInput] = useState('');
    const step = wizard[index];
    const remaining = wizard.length - (index + 1);

    const onAction = useCallback((submit: boolean) => {
        if (submit) {
            const result = step.handler(input);
            if (result === undefined) throw new Error('TODO: Handle handler failure.');

            const newValue = value.concat(result);

            if (remaining) {
                setValue(newValue);
                setIndex(index + 1);
                setInput('');
            }
            else {
                callback(newValue);
            }
        }
        else {
            callback(null);
        }
    }, [index, value, input, wizard, callback]);

    const onKeyPress = useCallback((key: string) => {
        if (key === 'Enter') {
            onAction(true);
        }
    }, [onAction]);

    return <Prompt ignoreEnter onAction={onAction}>
        <Mount>
            <Float>
                <Input autoFocus
                    role="textbox"
                    value={input}
                    onKeyPress={e => onKeyPress(e.key)}
                    onChange={e => setInput(e.target.value)} />
                <List count={0}>
                    <Item>
                        {step.message}
                    </Item>
                </List>
            </Float>
        </Mount>
    </Prompt>;
};

const Controller = () => {

    const { callback } = useContext(GoContext);
    const [item, setItem] = useState(null as GoItem | null);
    const [params, setParams] = useState(null as any[] | null);

    useEffect(() => {
        if (item && (!!params || !item?.wizard?.length)) {
            callback({ success: true, item, params: params || [] });
        }
    }, [item, params, callback]);

    const handleResponse = (x: any, setter: any) => {
        if (x) setter(x);
        else callback({ success: false });
    };

    if (!item) return <Selector callback={x => handleResponse(x, setItem)} />;
    if (!params && item?.wizard?.length) return <Wizard wizard={item.wizard} callback={x => handleResponse(x, setParams)} />;

    return <></>;
};

export class GoPalette extends React.Component<{ provider: GoProvider, callback: GoCallback }> {

    public render() {
        const { provider, callback } = this.props;
        return (
            <GoContext.Provider value={{ provider, callback }}>
                <Controller />
            </GoContext.Provider>
        );
    }
}
